import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import CSS from 'csstype';
import { debounce } from 'lodash-es';
import React, { useMemo, useState } from 'react';

import { BracketIcon } from '~/src/dash/components/icons/misc-icons';
import {
  borderRadius,
  Div,
  fontSizing,
  Text,
  TKey,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { isLanguageOversized } from '~/src/locales/common';

type InputSize = 'mini' | 'extraSmall' | 'small';

type ISizeStyles = Record<InputSize, ISizeStyle>;

interface ISizeStyle {
  inputStyle: CSS.Properties;
  iconWrapperStyle: CSS.Properties;
}

const sizeStyles: ISizeStyles = {
  mini: {
    inputStyle: { fontSize: fontSizing.mini },
    iconWrapperStyle: { top: '5px' },
  },
  extraSmall: {
    inputStyle: { fontSize: fontSizing.extraSmall },
    iconWrapperStyle: { top: '6px' },
  },
  small: {
    inputStyle: { fontSize: fontSizing.small },
    iconWrapperStyle: { top: '7px' },
  },
};

type TextInputPrefix = 'search' | 'search_alt' | 'usd';

interface ITextInputProps {
  enableClear?: boolean; // displays clear button on right
  fullWidth?: boolean;
  handleOnChange: (text: string) => void;
  initialValue?: string;
  isDisabled?: boolean;
  inputSize?: InputSize;
  inputStyle?: CSS.Properties;
  maxLength?: number;
  minLength?: number;
  msDelay?: number; // add delay to debounce. default 0. recommended 300 or 500 if needing debounce
  placeholder?: string;
  placeholderTKey?: TKey; // if used, will override placeholder
  prefix?: TextInputPrefix; // displays icon/symbol on left
  width?: string;
  isAutoFocused?: boolean;
  type?: string;
  max?: number;
  min?: number;
  step?: number;
  inputID?: string;
  className?: string;
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void; // when user leaves the input field, update it with the value from this custom onBlur
}

export const TextInput = ({
  enableClear,
  fullWidth,
  handleOnChange,
  isAutoFocused,
  isDisabled = false,
  initialValue,
  inputSize = 'extraSmall',
  inputStyle = {},
  maxLength = 524288, // true input default
  minLength = 0,
  msDelay = 0,
  placeholder = '',
  placeholderTKey,
  prefix,
  width = '240px',
  type = 'text',
  max,
  min,
  step,
  inputID,
  className = 'text-input-base',
  onBlur,
}: ITextInputProps) => {
  const { t, i18n } = usePublicaTranslation();
  const [input, setInput] = useState(initialValue || '');

  const debouncedOnChange = useMemo(() => {
    return debounce(handleOnChange, msDelay);
  }, [handleOnChange]);

  const onChange = (value: string) => {
    if (isDisabled) return;
    setInput(value);
    debouncedOnChange(value);
  };

  const onPaste = (
    e: React.ClipboardEvent<HTMLInputElement> | React.ClipboardEvent<HTMLTextAreaElement>,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    const clipboardData = e.clipboardData?.getData('Text') || '';
    const { selectionStart, selectionEnd, value } = e.currentTarget || {};
    if (selectionStart === selectionEnd) {
      if (selectionStart === 0) {
        onChange(clipboardData + value);
        return;
      }
      if ((selectionStart || 0) >= value.length) {
        onChange(value + clipboardData);
        return;
      }
    }
    onChange(
      value.substring(0, selectionStart || 0) +
        clipboardData +
        value.substring(selectionEnd || 0, value.length + 1),
    );
  };

  const getAdjustedInputSize = (): InputSize => {
    if (isLanguageOversized(i18n.language)) {
      switch (inputSize) {
        case 'extraSmall':
          return 'mini';
        case 'small':
          return 'extraSmall';
        default:
          return 'extraSmall';
      }
    }
    switch (inputSize) {
      case 'extraSmall':
        return 'extraSmall';
      case 'small':
        return 'small';
      default:
        return 'extraSmall';
    }
  };

  const adjustedInputSize = getAdjustedInputSize();
  const sizeStyle = sizeStyles[adjustedInputSize];

  return (
    <Div style={{ width: fullWidth ? '100%' : width }}>
      <Div style={{ width: '100%' }}>
        <input
          autoFocus={isAutoFocused}
          className={className}
          type={type}
          max={max}
          min={min}
          step={step}
          placeholder={placeholderTKey ? t(placeholderTKey) : placeholder}
          value={input}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            e.stopPropagation();
            e.preventDefault();
            onChange(e.currentTarget.value);
          }}
          onPaste={onPaste}
          maxLength={maxLength}
          minLength={minLength}
          style={{
            background: isDisabled ? publicaColors.veryLightGray : publicaColors.white,
            border: `0.5px solid ${publicaColors.lightGray}`,
            borderRadius: borderRadius.small,
            boxShadow: isDisabled ? 'none' : 'inset 1px 2px 8px rgba(0, 0, 0, 0.05)',
            color: isDisabled ? publicaColors.gray : publicaColors.veryDarkGray,
            pointerEvents: isDisabled ? 'none' : 'all',
            fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif`,
            paddingTop: '6px',
            paddingRight: enableClear ? '30px' : '12px',
            paddingBottom: '6px',
            paddingLeft: prefix ? '30px' : '12px',
            width: '100%',
            ...sizeStyle.inputStyle,
            ...inputStyle,
          }}
          id={inputID || 'text-input-base'}
          onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (onBlur) {
              e.stopPropagation();
              e.preventDefault();
              onBlur(e);
              onChange(e.currentTarget.value);
            }
          }}
        />
      </Div>
      {prefix && (
        <Div
          style={{
            position: 'absolute',
            left: '6px',
            height: '16px',
            ...sizeStyle.iconWrapperStyle,
          }}
        >
          {prefix === 'search' && (
            <SearchIcon
              style={{
                color: publicaColors.gray,
                height: '16px',
              }}
            />
          )}
          {prefix === 'search_alt' && (
            <BracketIcon
              color={publicaColors.gray}
              style={{
                height: '16px',
                marginLeft: '6px',
                paddingBottom: '1px',
              }}
            />
          )}
          {prefix === 'usd' && (
            <Text
              color={publicaColors.gray}
              style={{
                height: '16px',
                right: '5px',
                bottom: '4px',
                marginLeft: '3px',
                position: 'relative',
                fontWeight: 500,
              }}
            >
              $
            </Text>
          )}
        </Div>
      )}
      {enableClear && input !== '' && (
        <Div
          onClick={() => {
            onChange('');
          }}
          style={{
            position: 'absolute',
            right: '6px',
            height: '16px',
            ...sizeStyle.iconWrapperStyle,
          }}
        >
          <ClearIcon
            style={{
              color: publicaColors.gray,
              height: '16px',
            }}
          />
        </Div>
      )}
    </Div>
  );
};
